import apiClient from './client';
import type {
  PostSessionCommitment,
  PostSessionEngagementRating,
  PostSessionParticipant,
  PostSessionSessionWithDetails,
  UpsertCommitmentsRequest,
  UpsertCommitmentsResponse,
} from './coachPostSession';
import type {
  EngagementRatingInput,
  ParticipantAttendanceInput,
} from './coachPortal';

/**
 * MED-817 / E4.5 — Peer Leader Post-Session API client.
 *
 * Mounted under `/api/participant/sessions/:sessionId/post-session/...`.
 *
 * The peer leader gets the full coach post-session structure (a role-aware
 * delta on MED-794), so the bundle mirrors the coach `PostSessionBundle`
 * shape and reuses the coach type mirrors. Two peer-leader-specific additions:
 *   - `leader_user_id` flags the leader's own ("You") row (AC5/8).
 *   - `next_session_callout` drives AC14 (null unless the leader leads next).
 *
 * Attendance + commitments write through peer-leader endpoints that reuse the
 * coach backend tables (IV2/IV3). Session Notes carry-forward (AC6) reuses the
 * coach `coachActiveSessionApi.updateCoachNotes` endpoint directly (the peer
 * leader is authorized there via assertSessionLeaderOrCoach).
 */

export interface PeerLeaderSessionFeedback {
  id: string;
  session_id: string;
  user_id: string;
  responses: Record<string, string>;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NextSessionCallout {
  session_id: string;
  session_number: number;
  session_date: string | null;
}

export interface PeerLeaderPostSessionBundle {
  session: PostSessionSessionWithDetails;
  participants: PostSessionParticipant[];
  engagement_ratings: PostSessionEngagementRating[];
  feedback: PeerLeaderSessionFeedback | null;
  commitments: PostSessionCommitment[];
  /** The peer leader's own user id — flags the "You" row in attendance + commitments. */
  leader_user_id: string;
  /**
   * MED-1181 — the cohort's group-coaching per-modality Live Session Delivery
   * gate. Lets the page mirror its five sibling participant routes and fail
   * closed (defense-in-depth) when false, on top of the backend hard-block.
   */
  live_session_delivery_enabled: boolean;
  ai_features_enabled: boolean;
  /**
   * MED-1128 — session-level AI listening (`sessions.ai_listening_enabled`).
   * Post-session AI (recap) needs BOTH this AND `ai_features_enabled`; when
   * listening was off, the recap uses the plain template (same as modality-off)
   * plus an inline note. Mirrors the coach bundle.
   */
  ai_listening_enabled: boolean;
  next_session_date: string | null;
  recap_email_default_template: string | null;
  /** AC14 — set only when this leader is the designated leader of the next upcoming session. */
  next_session_callout: NextSessionCallout | null;
  /**
   * MED-894 (B7) — aggregate airtime split for the Airtime Balance card
   * (session-leader view). Null when AI wasn't active (card absent, AC7).
   */
  airtime: AirtimeEnvelope | null;
  /** MED-1187 (AC8) — a persisted transcript row exists (Delete-transcript control). */
  has_transcript: boolean;
  /** MED-1193 (B9) — session themes, PLAIN register only (server-scoped). */
  themes: {
    status: 'available' | 'pending' | 'not_available';
    themes?: Array<{ title: string; description: string }>;
  } | null;
  /**
   * MED-1194 (B10) — In-session insights (aggregate airtime tier only; the
   * one-dominating message is coach-only and never reaches this wire).
   */
  insession_insights: {
    status: 'available' | 'not_available';
    messages?: Array<{
      /** Narrower than the coach wire on purpose — dominating never reaches this view. */
      kind: 'airtime_split';
      title: string;
      text: string;
    }>;
  } | null;
  /**
   * MED-1213 — the session leader's durable recap-email draft edit, persisted
   * server-side (Case 2). Null until edited; mirrors the coach bundle field.
   */
  recap_draft_edit: { subject: string | null; body_html: string | null } | null;
}

/** MED-894 (B7) — same envelope as the coach bundle; see coachPostSession.ts. */
export interface AirtimeEnvelope {
  status: 'available' | 'pending' | 'not_available';
  split?: {
    leader_kind: 'coach' | 'peer_leader';
    leader_share_pct: number;
    participants_share_pct: number;
    leader_seconds: number;
    participants_seconds: number;
  };
}

export interface UpsertFeedbackRequest {
  responses: Record<string, string>;
}

export interface UpsertFeedbackResponse {
  feedback: PeerLeaderSessionFeedback;
}

export interface MarkAttendanceRequest {
  attendance: ParticipantAttendanceInput[];
  send_surveys: boolean;
  engagement_ratings?: EngagementRatingInput[];
}

export interface MarkAttendanceResponse {
  attendance_count: number;
  surveys_triggered: boolean;
}

export interface SendRecapEmailRequest {
  recipient_user_ids: string[];
  subject: string;
  body_html: string;
}

export interface SendRecapEmailResponse {
  sent: number;
  failed: number;
  skipped_not_in_session: number;
}

// MED-1213 — durable recap-email draft edit (idempotent upsert).
export interface SaveRecapDraftEditRequest {
  subject: string;
  body_html: string;
}

export interface SaveRecapDraftEditResponse {
  subject: string;
  body_html: string;
}

export const peerLeaderPostSessionApi = {
  getBundle: (sessionId: string): Promise<PeerLeaderPostSessionBundle> =>
    apiClient.get<PeerLeaderPostSessionBundle>(
      `/api/participant/sessions/${sessionId}/post-session`,
    ),

  upsertFeedback: (
    sessionId: string,
    body: UpsertFeedbackRequest,
  ): Promise<UpsertFeedbackResponse> =>
    // NOTE: `leader-feedback`, not `feedback` — the participant E3.4 routes
    // (MED-806) own `/post-session/feedback` on the same `/participant/sessions`
    // mount, so the peer leader form uses a disambiguated path.
    apiClient.put<UpsertFeedbackResponse>(
      `/api/participant/sessions/${sessionId}/post-session/leader-feedback`,
      body,
    ),

  markAttendance: (
    sessionId: string,
    body: MarkAttendanceRequest,
  ): Promise<MarkAttendanceResponse> =>
    apiClient.put<MarkAttendanceResponse>(
      `/api/participant/sessions/${sessionId}/post-session/attendance`,
      body,
    ),

  upsertCommitments: (
    sessionId: string,
    body: UpsertCommitmentsRequest,
  ): Promise<UpsertCommitmentsResponse> =>
    apiClient.put<UpsertCommitmentsResponse>(
      `/api/participant/sessions/${sessionId}/post-session/commitments`,
      body,
    ),

  sendRecapEmail: (
    sessionId: string,
    body: SendRecapEmailRequest,
  ): Promise<SendRecapEmailResponse> =>
    apiClient.post<SendRecapEmailResponse>(
      `/api/participant/sessions/${sessionId}/post-session/recap-email/send`,
      body,
    ),

  /** PUT idempotent upsert of the leader's durable recap draft edit (MED-1213). */
  saveRecapDraftEdit: (
    sessionId: string,
    body: SaveRecapDraftEditRequest,
  ): Promise<SaveRecapDraftEditResponse> =>
    apiClient.put<SaveRecapDraftEditResponse>(
      `/api/participant/sessions/${sessionId}/post-session/recap-email/draft`,
      body,
    ),
};
